/**
 * MatchDetailPage — Public scorecard for a single fixture.
 * Route: /tournaments/:eventId/match/:fixtureId
 *
 * Sections:
 *   - Header (teams, venue, status, winner)
 *   - Per-innings tabs with batting/bowling/extras/fall-of-wickets/partnerships
 *   - Over-by-over ball log (collapsible)
 *   - Match events (highlights)
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import useLiveMatchSocket from '../components/live-scoring/useLiveMatchSocket';
import { LiveIndicator } from '../components/live-scoring/LivePhase';

function fmtOvers(balls) {
  if (!balls) return '0.0';
  const full = Math.floor(balls / 6);
  const rem = balls % 6;
  return `${full}.${rem}`;
}

function fmtDate(iso) {
  if (!iso) return 'TBD';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function BallPill({ ball }) {
  let label = String(ball.runs_scored || 0);
  let cls = 'bg-gray-100 text-gray-700';
  if (ball.is_wicket) { label = 'W'; cls = 'bg-red-500 text-white'; }
  else if (ball.is_boundary_six) { label = '6'; cls = 'bg-purple-500 text-white'; }
  else if (ball.is_boundary_four) { label = '4'; cls = 'bg-blue-500 text-white'; }
  else if (ball.extras_type === 'wide') { label = `Wd${ball.extras_runs > 1 ? '+' + (ball.extras_runs - 1) : ''}`; cls = 'bg-amber-400 text-white'; }
  else if (ball.extras_type === 'no_ball') { label = `Nb${ball.extras_runs > 1 ? '+' + (ball.extras_runs - 1) : ''}`; cls = 'bg-orange-400 text-white'; }
  else if (ball.extras_type === 'bye') { label = `b${ball.extras_runs}`; cls = 'bg-emerald-300 text-emerald-900'; }
  else if (ball.extras_type === 'leg_bye') { label = `lb${ball.extras_runs}`; cls = 'bg-teal-300 text-teal-900'; }
  else if ((ball.runs_scored || 0) === 0) { label = '•'; cls = 'bg-gray-200 text-gray-500'; }
  return (
    <span
      className={`min-w-[28px] h-7 px-2 rounded-full text-xs font-bold inline-flex items-center justify-center ${cls}`}
      title={ball.commentary || ''}
      data-testid={`ball-pill-${ball.ball_sequence}`}
    >{label}</span>
  );
}

export default function MatchDetailPage() {
  const { eventId, fixtureId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({
    fixture: null, innings: [], stats: [], partnerships: [], events: [], balls: [], awards: [],
  });
  const [loading, setLoading] = useState(true);
  const [activeInnings, setActiveInnings] = useState(1);
  const [expandedOvers, setExpandedOvers] = useState({}); // { [inningsId_overNumber]: bool }

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [fixtureRes, inningsRes, statsRes, partsRes, eventsRes, awardsRes] = await Promise.all([
        axiosInstance.get(`/v1/events/${eventId}/fixtures/${fixtureId}`),
        axiosInstance.get(`/v1/events/${eventId}/live/${fixtureId}/innings`).catch(() => ({ data: [] })),
        axiosInstance.get(`/v1/events/${eventId}/live/${fixtureId}/stats`).catch(() => ({ data: [] })),
        axiosInstance.get(`/v1/events/${eventId}/live/${fixtureId}/partnerships`).catch(() => ({ data: [] })),
        axiosInstance.get(`/v1/events/${eventId}/live/${fixtureId}/events`).catch(() => ({ data: [] })),
        axiosInstance.get(`/v1/events/${eventId}/fixtures/${fixtureId}/awards`).catch(() => ({ data: [] })),
      ]);

      // Fetch all balls for each innings
      const ballsByInnings = {};
      for (const inn of (inningsRes.data || [])) {
        try {
          const r = await axiosInstance.get(
            `/v1/events/${eventId}/live/${fixtureId}/innings/${inn.id}/balls`,
            { params: { limit: 500 } }
          );
          ballsByInnings[inn.id] = r.data || [];
        } catch { ballsByInnings[inn.id] = []; }
      }

      setData({
        fixture: fixtureRes.data,
        innings: inningsRes.data || [],
        stats: statsRes.data || [],
        partnerships: partsRes.data || [],
        events: eventsRes.data || [],
        awards: awardsRes.data || [],
        ballsByInnings,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [eventId, fixtureId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Subscribe to realtime updates when the match is live (polling fallback is
  // automatic inside the hook). For completed matches we just load once.
  const isLiveForWs = data.fixture?.match_status === 'live';
  const { connected: wsConnected } = useLiveMatchSocket({
    eventId,
    fixtureId,
    enabled: isLiveForWs,
    onUpdate: useCallback(() => { fetchAll(); }, [fetchAll]),
  });
  const showLivePill = useMemo(() => isLiveForWs, [isLiveForWs]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AAFA9]"></div>
      </div>
    );
  }

  const { fixture, innings, stats, partnerships, events, ballsByInnings = {} } = data;
  // Over-by-over summaries belong to the live feed; the ball-by-ball section covers overs here.
  const highlights = events.filter(ev => ev.event_type !== 'over_summary');
  if (!fixture) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Match not found.</p>
          <button onClick={() => navigate(-1)} className="px-4 py-2 bg-[#3AAFA9] text-white rounded-lg">Go back</button>
        </div>
      </div>
    );
  }

  const inningsForTab = innings.find(i => i.innings_number === activeInnings) || innings[0];
  const statsFor = (teamId) => stats.filter(s => s.team_id === teamId);
  const partnershipsFor = (inningsId) =>
    partnerships.filter(p => p.innings_id === inningsId).sort((a, b) => a.wicket_number - b.wicket_number);

  const isLive = fixture.match_status === 'live';
  const isDone = fixture.match_status === 'completed';
  const winnerName = fixture.winner_team_id === fixture.team_a_id
    ? fixture.team_a_name
    : fixture.winner_team_id === fixture.team_b_id
      ? fixture.team_b_name
      : null;

  // Fall of wickets (from balls) for an innings
  const fallOfWickets = (inningsId) => {
    const balls = (ballsByInnings[inningsId] || []).filter(b => b.is_wicket);
    let running = 0, legalBalls = 0;
    // Walk all balls in order to compute score-at-wicket
    const allBalls = ballsByInnings[inningsId] || [];
    const out = [];
    for (const b of allBalls) {
      const bat = b.runs_scored || 0;
      const ext = b.extras_runs || 0;
      const ot = b.overthrow_runs || 0;
      if (b.extras_type === 'wide') running += ext + ot;
      else if (b.extras_type === 'no_ball') running += bat + ext + ot;
      else if (b.extras_type === 'bye' || b.extras_type === 'leg_bye' || b.extras_type === 'penalty') running += ext;
      else running += bat + ot;
      if (b.is_legal_delivery) legalBalls++;
      if (b.is_wicket && b.wicket_type !== 'retired_hurt') {
        out.push({
          player: b.dismissed_player_name,
          score: running,
          wicket_number: out.length + 1,
          overs: fmtOvers(legalBalls),
          wicket_type: b.wicket_type,
        });
      }
    }
    return out;
  };

  // Group balls by over for the ball-log section
  const groupByOver = (inningsId) => {
    const balls = (ballsByInnings[inningsId] || []).slice().sort((a, b) => a.ball_sequence - b.ball_sequence);
    const groups = {};
    for (const b of balls) {
      const key = b.over_number;
      if (!groups[key]) groups[key] = [];
      groups[key].push(b);
    }
    return Object.entries(groups).sort(([a], [b]) => Number(a) - Number(b));
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-16" data-testid="match-detail-page">
      {/* Header */}
      <div className="bg-[#2B3A67] text-white px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="text-white/70 hover:text-white text-sm flex items-center gap-1 mb-3"
            data-testid="back-btn"
          >
            <i className="ph-bold ph-arrow-left"></i> Back
          </button>

          <div className="flex items-center justify-between gap-2 text-xs text-white/60 mb-2">
            <span>Match #{fixture.match_number} • {fixture.round_type?.replace('_', ' ') || 'League'}</span>
            <div className="flex items-center gap-2">
              {showLivePill && (
                <span
                  data-testid="live-ws-status"
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    wsConnected ? 'bg-white/15 text-white' : 'bg-white/5 text-white/60'
                  }`}
                  title={wsConnected ? 'Live updates connected' : 'Reconnecting — auto-refreshing every 30s'}
                >
                  <span className="relative inline-flex h-2 w-2">
                    {wsConnected && (
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    )}
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${wsConnected ? 'bg-red-500' : 'bg-white/40'}`}></span>
                  </span>
                  {wsConnected ? 'Streaming' : 'Reconnecting…'}
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-full font-semibold ${
                isLive ? 'bg-red-500 text-white animate-pulse' :
                isDone ? 'bg-emerald-500 text-white' :
                'bg-white/20 text-white/80'
              }`}>
                {fixture.match_status?.toUpperCase()}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 items-center gap-4 mt-3">
            <div className="text-right" data-testid="team-a-block">
              <div className="font-bold text-lg truncate">{fixture.team_a_name || 'TBD'}</div>
              <div className="text-2xl font-extrabold">
                {fixture.team_a_score != null ? `${fixture.team_a_score}/${fixture.team_a_wickets || 0}` : '—'}
              </div>
              <div className="text-xs text-white/60">
                {fixture.team_a_overs != null ? `${fixture.team_a_overs} ov` : ''}
              </div>
            </div>
            <div className="text-center text-white/60 font-bold">VS</div>
            <div className="text-left" data-testid="team-b-block">
              <div className="font-bold text-lg truncate">{fixture.team_b_name || 'TBD'}</div>
              <div className="text-2xl font-extrabold">
                {fixture.team_b_score != null ? `${fixture.team_b_score}/${fixture.team_b_wickets || 0}` : '—'}
              </div>
              <div className="text-xs text-white/60">
                {fixture.team_b_overs != null ? `${fixture.team_b_overs} ov` : ''}
              </div>
            </div>
          </div>

          {fixture.result_summary && (
            <div className="mt-4 text-center text-sm font-medium text-emerald-300" data-testid="result-summary">
              <i className="ph-fill ph-trophy mr-1"></i>{fixture.result_summary}
            </div>
          )}
          {!fixture.result_summary && winnerName && (
            <div className="mt-4 text-center text-sm font-medium text-emerald-300">
              Winner: {winnerName}
            </div>
          )}

          <div className="mt-3 text-xs text-white/60 flex items-center justify-center gap-3 flex-wrap">
            <span><i className="ph-bold ph-calendar mr-1"></i>{fmtDate(fixture.scheduled_at)}</span>
            {fixture.venue_name && <span><i className="ph-bold ph-map-pin mr-1"></i>{fixture.venue_name}</span>}
            {fixture.turf_name && <span><i className="ph-bold ph-soccer-ball mr-1"></i>{fixture.turf_name}</span>}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 mt-6 space-y-6">
        {/* Match Awards — visible only when there are finalised awards */}
        <MatchAwardsBanner awards={(data.awards || []).filter(a => a.is_finalized)} />

        {innings.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center text-gray-500" data-testid="no-innings">
            This match has not started yet. The scorecard will appear once the first ball is bowled.
          </div>
        ) : (
          <>
            {/* Innings tabs */}
            <div className="flex gap-2" data-testid="innings-tabs">
              {innings.map(inn => (
                <button
                  key={inn.id}
                  onClick={() => setActiveInnings(inn.innings_number)}
                  className={`flex-1 py-2.5 rounded-lg font-bold text-sm transition-all ${
                    activeInnings === inn.innings_number
                      ? 'bg-[#2B3A67] text-white shadow'
                      : 'bg-white text-[#2B3A67] border border-gray-200 hover:bg-gray-50'
                  }`}
                  data-testid={`innings-tab-${inn.innings_number}`}
                >
                  Innings {inn.innings_number} — {inn.batting_team_name}
                  <span className="block text-xs font-semibold opacity-80">
                    {inn.total_runs}/{inn.total_wickets} ({inn.total_overs} ov)
                  </span>
                </button>
              ))}
            </div>

            {inningsForTab && (
              <>
                {/* Batting Scorecard */}
                <section className="bg-white rounded-xl shadow-sm p-4" data-testid="batting-scorecard">
                  <h3 className="font-bold text-[#2B3A67] mb-3 flex items-center gap-2">
                    <i className="ph-bold ph-baseball"></i>Batting
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-xs text-gray-500 uppercase border-b border-gray-100">
                        <tr>
                          <th className="text-left py-2 font-medium">Batsman</th>
                          <th className="text-left py-2 font-medium">Status</th>
                          <th className="text-right py-2 font-medium">R</th>
                          <th className="text-right py-2 font-medium">B</th>
                          <th className="text-right py-2 font-medium">4s</th>
                          <th className="text-right py-2 font-medium">6s</th>
                          <th className="text-right py-2 font-medium">SR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statsFor(inningsForTab.batting_team_id)
                          .filter(s => (s.batting_balls || 0) > 0 || s.how_out)
                          .map(s => (
                            <tr key={s.id} className="border-b border-gray-50 last:border-0">
                              <td className="py-2 font-medium text-gray-800">{s.player_name}</td>
                              <td className="py-2 text-xs text-gray-500">
                                {s.how_out ? `${s.how_out}${s.dismissed_by ? ` b ${s.dismissed_by}` : ''}` : 'not out'}
                              </td>
                              <td className="py-2 text-right font-semibold">{s.batting_runs}</td>
                              <td className="py-2 text-right">{s.batting_balls}</td>
                              <td className="py-2 text-right">{s.batting_fours}</td>
                              <td className="py-2 text-right">{s.batting_sixes}</td>
                              <td className="py-2 text-right text-gray-500">{s.batting_strike_rate?.toFixed(1) || '—'}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-3 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                    <span>Extras: {(inningsForTab.extras_wides + inningsForTab.extras_no_balls + inningsForTab.extras_byes + inningsForTab.extras_leg_byes + inningsForTab.extras_penalty)}</span>
                    <span>(Wd {inningsForTab.extras_wides},</span>
                    <span>Nb {inningsForTab.extras_no_balls},</span>
                    <span>B {inningsForTab.extras_byes},</span>
                    <span>Lb {inningsForTab.extras_leg_byes})</span>
                  </div>

                  <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between text-sm font-bold text-[#2B3A67]">
                    <span>Total</span>
                    <span>{inningsForTab.total_runs}/{inningsForTab.total_wickets} ({inningsForTab.total_overs} ov, RR {inningsForTab.run_rate?.toFixed(2) || '0.00'})</span>
                  </div>
                </section>

                {/* Bowling Scorecard */}
                <section className="bg-white rounded-xl shadow-sm p-4" data-testid="bowling-scorecard">
                  <h3 className="font-bold text-[#2B3A67] mb-3 flex items-center gap-2">
                    <i className="ph-bold ph-target"></i>Bowling
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-xs text-gray-500 uppercase border-b border-gray-100">
                        <tr>
                          <th className="text-left py-2 font-medium">Bowler</th>
                          <th className="text-right py-2 font-medium">O</th>
                          <th className="text-right py-2 font-medium">M</th>
                          <th className="text-right py-2 font-medium">R</th>
                          <th className="text-right py-2 font-medium">W</th>
                          <th className="text-right py-2 font-medium">Econ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statsFor(inningsForTab.bowling_team_id)
                          .filter(s => (s.bowling_overs || 0) > 0 || (s.bowling_runs || 0) > 0)
                          .map(s => (
                            <tr key={s.id} className="border-b border-gray-50 last:border-0">
                              <td className="py-2 font-medium text-gray-800">{s.player_name}</td>
                              <td className="py-2 text-right">{s.bowling_overs?.toFixed(1) || '0.0'}</td>
                              <td className="py-2 text-right">{s.bowling_maidens}</td>
                              <td className="py-2 text-right">{s.bowling_runs}</td>
                              <td className="py-2 text-right font-semibold">{s.bowling_wickets}</td>
                              <td className="py-2 text-right text-gray-500">{s.bowling_economy?.toFixed(2) || '—'}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Fall of Wickets */}
                {fallOfWickets(inningsForTab.id).length > 0 && (
                  <section className="bg-white rounded-xl shadow-sm p-4" data-testid="fall-of-wickets">
                    <h3 className="font-bold text-[#2B3A67] mb-3 flex items-center gap-2">
                      <i className="ph-bold ph-warning-circle"></i>Fall of Wickets
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {fallOfWickets(inningsForTab.id).map((w, idx) => (
                        <span key={idx} className="text-xs bg-red-50 text-red-700 rounded-full px-3 py-1">
                          <strong>{w.score}/{w.wicket_number}</strong> ({w.player}, {w.overs})
                        </span>
                      ))}
                    </div>
                  </section>
                )}

                {/* Partnerships */}
                {partnershipsFor(inningsForTab.id).length > 0 && (
                  <section className="bg-white rounded-xl shadow-sm p-4" data-testid="partnerships">
                    <h3 className="font-bold text-[#2B3A67] mb-3 flex items-center gap-2">
                      <i className="ph-bold ph-handshake"></i>Partnerships
                    </h3>
                    <div className="space-y-2">
                      {partnershipsFor(inningsForTab.id).map((p, idx) => (
                        <div key={p.id} className="border border-gray-100 rounded-lg p-2 text-sm">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-gray-700">
                              {idx + 1}. {p.batsman1_name} &amp; {p.batsman2_name}
                            </span>
                            <span className="font-bold text-[#2B3A67]">
                              {p.total_runs} ({p.total_balls})
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {p.batsman1_name}: {p.batsman1_runs}({p.batsman1_balls}) &nbsp;•&nbsp;
                            {p.batsman2_name}: {p.batsman2_runs}({p.batsman2_balls})
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Ball-by-ball log */}
                <section className="bg-white rounded-xl shadow-sm p-4" data-testid="ball-log">
                  <h3 className="font-bold text-[#2B3A67] mb-3 flex items-center gap-2">
                    <i className="ph-bold ph-list-numbers"></i>Ball-by-Ball
                  </h3>
                  <div className="space-y-2">
                    {groupByOver(inningsForTab.id).map(([overNumber, balls]) => {
                      const key = `${inningsForTab.id}_${overNumber}`;
                      const expanded = expandedOvers[key];
                      const overRuns = balls.reduce((sum, b) => {
                        const bat = b.runs_scored || 0;
                        const ext = b.extras_runs || 0;
                        const ot = b.overthrow_runs || 0;
                        if (b.extras_type === 'wide') return sum + ext + ot;
                        if (b.extras_type === 'no_ball') return sum + bat + ext + ot;
                        if (['bye', 'leg_bye', 'penalty'].includes(b.extras_type)) return sum + ext;
                        return sum + bat + ot;
                      }, 0);
                      const overWickets = balls.filter(b => b.is_wicket && b.wicket_type !== 'retired_hurt').length;
                      return (
                        <div key={key} className="border border-gray-100 rounded-lg" data-testid={`over-${overNumber}`}>
                          <button
                            onClick={() => setExpandedOvers(prev => ({ ...prev, [key]: !prev[key] }))}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <span className="text-xs font-bold text-gray-500 flex-shrink-0">Over {Number(overNumber) + 1}</span>
                              <span className="text-xs text-gray-500 flex-shrink-0">
                                {overRuns} run{overRuns !== 1 ? 's' : ''}{overWickets > 0 ? ` • ${overWickets}W` : ''}
                                <i className={`ph-bold ${expanded ? 'ph-caret-up' : 'ph-caret-down'} ml-2`}></i>
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-1">
                              {balls.map(b => <BallPill key={b.id} ball={b} />)}
                            </div>
                          </button>
                          {expanded && (
                            <div className="border-t border-gray-100 px-3 py-2 space-y-1 text-xs">
                              {balls.map(b => (
                                <div key={b.id} className="flex gap-3 py-0.5">
                                  <span className="font-mono text-gray-500 w-12 flex-shrink-0">
                                    {b.over_number}.{b.ball_number || '-'}
                                  </span>
                                  <span className="text-gray-700 flex-1">{b.commentary || '—'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {groupByOver(inningsForTab.id).length === 0 && (
                      <div className="text-xs text-gray-400 italic text-center py-3">No balls recorded in this innings yet.</div>
                    )}
                  </div>
                </section>
              </>
            )}
          </>
        )}

        {/* Match Events / Highlights */}
        {highlights.length > 0 && (
          <section className="bg-white rounded-xl shadow-sm p-4" data-testid="match-events">
            <h3 className="font-bold text-[#2B3A67] mb-3 flex items-center gap-2">
              <i className="ph-bold ph-star"></i>Highlights
            </h3>
            <div className="space-y-1.5">
              {highlights.slice(0, 15).map(ev => (
                ['innings_summary', 'match_summary'].includes(ev.event_type) ? (
                  <div key={ev.id} className="text-sm border-l-2 border-[#3AAFA9] pl-3 py-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{new Date(ev.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      <span className="font-medium text-gray-800">{ev.title}</span>
                    </div>
                    {ev.description && <p className="text-xs text-gray-600 mt-0.5">{ev.description}</p>}
                  </div>
                ) : (
                <div
                  key={ev.id}
                  className="flex items-center gap-2 text-sm border-l-2 border-[#3AAFA9] pl-3 py-1"
                >
                  <span className="text-xs text-gray-400">{new Date(ev.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  <span className="font-medium text-gray-800">{ev.title}</span>
                  {ev.description && <span className="text-xs text-gray-500 truncate">· {ev.description}</span>}
                </div>
                )
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// ── Match Awards banner (public scorecard) ────────────────────────────────
function MatchAwardsBanner({ awards }) {
  if (!awards || awards.length === 0) return null;
  const potm = awards.find(a => a.award_type === 'player_of_match');
  const rest = awards.filter(a => a.award_type !== 'player_of_match');

  return (
    <div
      className="rounded-2xl overflow-hidden border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50 shadow-sm"
      data-testid="match-awards-banner"
    >
      <div className="px-4 py-3 border-b border-amber-200/60 flex items-center gap-2 text-amber-800">
        <i className="ph-fill ph-trophy"></i>
        <span className="text-sm font-bold uppercase tracking-wider">Match Awards</span>
      </div>
      <div className="p-4 space-y-3">
        {potm && (
          <div
            className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md"
            data-testid={`match-award-${potm.award_type}`}
          >
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white">
              <i className={`ph-fill ${potm.icon || 'ph-star'} text-2xl`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-wider text-amber-100">
                Player of the Match
              </div>
              <div className="text-lg font-bold truncate">{potm.winner_player_name || '—'}</div>
              <div className="text-xs text-amber-100 truncate">
                {potm.winner_team_name}
                {potm.stat_value && <span> · {potm.stat_value}</span>}
              </div>
            </div>
          </div>
        )}
        {rest.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {rest.map(a => (
              <div
                key={a.id}
                data-testid={`match-award-${a.award_type}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100"
              >
                <div className="w-9 h-9 rounded-full bg-[#3AAFA9]/10 flex items-center justify-center text-[#3AAFA9]">
                  <i className={`ph-bold ${a.icon || 'ph-medal'}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                    {a.title}
                  </div>
                  <div className="text-sm font-bold text-[#2B3A67] truncate">
                    {a.winner_player_name || '—'}
                  </div>
                  {a.stat_value && (
                    <div className="text-[11px] text-gray-500 truncate">{a.stat_value}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

